// Rug Room Shots: the "Room type rules" panel of the Rooms tab (build spec 3.2, 5.5 and 9.6).
// Which room types (the 7th letter of a room code) a rug may use: small rounds, large rounds, shag accent rugs,
// other accent rugs and shag runners. GET /room-rules gives the stored change, the code defaults, the effective
// rules and their version; the worker reports the version it uses. PUT replaces the stored object with the keys
// that differ from the defaults; DELETE resets to the defaults. The worker re-applies the rules to unstarted plans
// within a few minutes. Keys stay snake_case (they are setting names).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, TextField, Alert, CircularProgress, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Collapse, Table, TableBody, TableRow, TableCell, Tooltip,
} from '@mui/material';
import { MdEdit, MdRestartAlt, MdExpandMore, MdExpandLess, MdRefresh } from 'react-icons/md';
import rrsApi from './api';
import { primaryButtonSx, NyTime, ConfirmDialog, serverText } from './common';
import { effectiveRuleLines, validLetters } from './roomRules';

const LETTER_KEYS = ['round_small_letters', 'round_large_letters', 'shag_accent_letters', 'accent_other_letters', 'shag_runner_letters'];
const LETTERS_HELP = 'One or more letters; > means else (L>E = living room, else entry); !B = any type except bedroom.';

// The code defaults (spec 3.2), used only until GET /room-rules answers.
const FALLBACK_DEFAULTS = {
  round_small_max: 6,
  round_small_letters: 'L',
  round_large_letters: 'D',
  shag_rug_types: ['Shag'],
  shag_accent_sizes: ['2', '3'],
  shag_accent_letters: 'B',
  accent_other_letters: '!B',
  shag_runner_letters: 'H',
  shag_rules_skip_styles: ['OU', 'SU'],
};

const FIELDS = [
  { key: 'round_small_max', label: 'Largest small round (feet)', kind: 'int', help: 'Rounds up to this size use the small-round rooms (1-20).' },
  { key: 'round_small_letters', label: 'Small rounds: room letters', kind: 'letters' },
  { key: 'round_large_letters', label: 'Larger rounds: room letters', kind: 'letters' },
  { key: 'shag_accent_sizes', label: 'Shag accent sizes', kind: 'list', help: 'Comma-separated size numbers from the ItemID, e.g. 2, 3 (-2, -2W, -3S ...). Up to 10.' },
  { key: 'shag_accent_letters', label: 'Shag accent rugs: room letters', kind: 'letters' },
  { key: 'accent_other_letters', label: 'Other accent rugs: room letters', kind: 'letters' },
  { key: 'shag_runner_letters', label: 'Shag runners: room letters', kind: 'letters' },
  { key: 'shag_rug_types', label: 'Shag means rug type', kind: 'list', help: 'Comma-separated rug types from the catalog, e.g. Shag. 1 to 5.' },
  { key: 'shag_rules_skip_styles', label: 'Styles the accent and runner rules skip', kind: 'list', help: 'Comma-separated style codes, e.g. OU, SU. Up to 10.' },
];

const splitList = (s) => String(s || '').split(',').map((x) => x.trim()).filter(Boolean);
const listText = (v) => (Array.isArray(v) ? v.join(', ') : String(v || ''));

// Validation per spec 3.2; returns { value, error }.
const parseField = (f, raw) => {
  if (f.kind === 'int') {
    const n = Number(String(raw).trim());
    if (!Number.isInteger(n) || n < 1 || n > 20) return { error: 'A whole number from 1 to 20.' };
    return { value: n };
  }
  if (f.kind === 'letters') {
    const v = String(raw || '').trim().toUpperCase();
    if (!validLetters(v)) return { error: 'Letters A-Z, e.g. L, L>E, LE or !B (empty = rule off).' };
    if (v.length > 16) return { error: 'At most 16 characters.' };
    return { value: v };
  }
  const items = splitList(raw);
  if (f.key === 'shag_rug_types') {
    if (items.length < 1 || items.length > 5) return { error: '1 to 5 rug types.' };
    if (items.some((x) => x.length > 32 || !/^[A-Za-z /&-]+$/.test(x))) return { error: 'Letters, spaces, / & - only, up to 32 characters each.' };
    return { value: items };
  }
  if (f.key === 'shag_accent_sizes') {
    if (items.length > 10) return { error: 'Up to 10 sizes.' };
    if (items.some((x) => !/^\d{1,4}$/.test(x))) return { error: 'Size numbers only (1 to 4 digits), e.g. 2, 3.' };
    return { value: items };
  }
  const codes = items.map((x) => x.toUpperCase());
  if (codes.length > 10) return { error: 'Up to 10 style codes.' };
  if (codes.some((x) => !/^[A-Z]{2}$/.test(x))) return { error: 'Two-letter style codes, e.g. OU, SU.' };
  return { value: codes };
};

// Normalised for "differs from the default" (lists compare as sets, as the rules version does).
const norm = (key, v) => {
  if (key === 'round_small_max') return String(Number(v));
  if (key === 'shag_rug_types') return (v || []).map((x) => String(x).toLowerCase()).sort().join(',');
  if (key === 'shag_accent_sizes') return (v || []).map((x) => Number(x)).sort((a, b) => a - b).join(',');
  if (key === 'shag_rules_skip_styles') return (v || []).map((x) => String(x).toUpperCase()).sort().join(',');
  return String(v || '').toUpperCase();
};

const RoomRulesPanel = ({ notify, letters, onChanged, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [edit, setEdit] = useState(null); // { key: text }
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await rrsApi.getRoomRules();
      setData(res || null);
      setError('');
      return res;
    } catch (e) {
      setError(e && e.status === 404 ? 'The server has not been updated for room type rules yet.' : serverText(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const defaults = (data && data.defaults) || FALLBACK_DEFAULTS;
  const effective = (data && data.effective) || null;
  const changedKeys = useMemo(() => {
    const stored = (data && data.stored) || {};
    return Object.keys(stored).filter((k) => stored[k] !== undefined);
  }, [data]);
  const lines = useMemo(() => effectiveRuleLines(effective, letters), [effective, letters]);
  const workerBehind = !!(data && data.workerVersion && data.version && data.workerVersion !== data.version);
  const workerReady = !data || data.workerReady !== false;

  const changedFor = (lineKey) => {
    const map = {
      round_small: ['round_small_max', 'round_small_letters'],
      round_large: ['round_small_max', 'round_large_letters'],
      shag_accent: ['shag_accent_sizes', 'shag_accent_letters'],
      accent_other: ['accent_other_letters'],
      shag_runner: ['shag_runner_letters'],
      shag_rug_types: ['shag_rug_types'],
      shag_rules_skip_styles: ['shag_rules_skip_styles'],
    };
    return (map[lineKey] || []).some((k) => changedKeys.includes(k));
  };

  const openEdit = () => {
    const src = effective || defaults;
    const next = {};
    FIELDS.forEach((f) => { next[f.key] = f.kind === 'list' ? listText(src[f.key]) : String(src[f.key] === undefined || src[f.key] === null ? '' : src[f.key]); });
    setEdit(next);
    setEditError('');
  };

  const parsed = useMemo(() => {
    if (!edit) return null;
    const out = { values: {}, errors: {} };
    FIELDS.forEach((f) => {
      const r = parseField(f, edit[f.key]);
      if (r.error) out.errors[f.key] = r.error;
      else out.values[f.key] = r.value;
    });
    return out;
  }, [edit]);
  const editOk = !!parsed && Object.keys(parsed.errors).length === 0;

  const save = async () => {
    if (!editOk) return;
    // Only the keys that differ from the defaults: a key left out follows the default (spec 5.5).
    const body = {};
    FIELDS.forEach((f) => {
      const v = parsed.values[f.key];
      if (norm(f.key, v) !== norm(f.key, defaults[f.key])) body[f.key] = v;
    });
    setEditBusy(true);
    setEditError('');
    try {
      const res = await rrsApi.saveRoomRules(body);
      setData(res || null);
      setEdit(null);
      notify(Object.keys(body).length
        ? 'Room type rules saved. The worker re-plans unstarted rugs whose room no longer fits within a few minutes.'
        : 'Room type rules saved (all defaults).', 'success');
      if (onChanged) onChanged(res);
    } catch (e) {
      setEditError(serverText(e));
    } finally {
      setEditBusy(false);
    }
  };

  const doReset = async () => {
    setResetBusy(true);
    try {
      const res = await rrsApi.resetRoomRules();
      setData(res || null);
      setResetOpen(false);
      notify('Room type rules reset to the defaults.', 'success');
      if (onChanged) onChanged(res);
    } catch (e) {
      notify(serverText(e), 'error');
    } finally {
      setResetBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        <Button
          onClick={() => setOpen((v) => !v)} endIcon={open ? <MdExpandLess /> : <MdExpandMore />}
          sx={{ fontWeight: 700, textTransform: 'none', color: 'text.primary', px: 0.5 }}
        >
          Room type rules
        </Button>
        {data && (
          <Typography variant="caption" color="text.secondary">
            {changedKeys.length ? `${changedKeys.length} setting${changedKeys.length === 1 ? '' : 's'} changed from the defaults` : 'Defaults'}
            {data.version ? ` · version ${data.version}` : ''}
          </Typography>
        )}
        {workerBehind && <Chip size="small" color="warning" variant="outlined" label="the worker has not picked up the latest change yet" />}
        <Box sx={{ flex: 1 }} />
        {open && (
          <>
            {loading && <CircularProgress size={18} />}
            <Button size="small" startIcon={<MdRefresh />} onClick={load} disabled={loading}>Refresh</Button>
            <Button size="small" variant="outlined" startIcon={<MdEdit />} onClick={openEdit} disabled={!data}>Edit</Button>
            <Button size="small" color="warning" startIcon={<MdRestartAlt />} onClick={() => setResetOpen(true)} disabled={!data || !changedKeys.length}>
              Reset to defaults
            </Button>
          </>
        )}
      </Stack>
      <Collapse in={open} unmountOnExit>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          The room type is the 7th letter of a room code (MC26RO<strong>D</strong>01 = dining). Plans only use rooms of the
          right type for the rug; with no such room the row is a gap and nothing is made. A room picked by hand may be any
          type (the page warns first).
        </Typography>
        {!workerReady && <Alert severity="info" sx={{ mt: 1 }}>Takes effect when the worker is updated.</Alert>}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        {effective && (
          <Table size="small" sx={{ mt: 1, maxWidth: 820 }}>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.key}>
                  <TableCell sx={{ width: 330, color: 'text.secondary' }}>{l.label}</TableCell>
                  <TableCell>
                    {l.value}
                    {changedFor(l.key) && (
                      <Tooltip title="Changed from the default on this page">
                        <Chip size="small" label="changed" color="info" variant="outlined" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {data && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Version {data.version || '-'}; the worker uses {data.workerVersion || 'no version yet'}
            {workerBehind ? ' (it picks up the change within a few minutes)' : ''}.
            {data.updatedByEmail ? <> Last changed by {data.updatedByEmail}, <NyTime ts={data.updatedAt} variant="caption" />.</> : null}
          </Typography>
        )}
      </Collapse>

      <Dialog open={!!edit} onClose={editBusy ? undefined : () => setEdit(null)} maxWidth="sm" fullWidth>
        {edit && (
          <>
            <DialogTitle>
              Room type rules
              <Typography variant="body2" color="text.secondary">
                Letters: {LETTERS_HELP} Empty letters switch that rule off (any room type).
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                {FIELDS.map((f) => {
                  const err = parsed && parsed.errors[f.key];
                  const def = defaults[f.key];
                  const defText = Array.isArray(def) ? def.join(', ') : String(def === undefined || def === null ? '' : def);
                  return (
                    <TextField
                      key={f.key} size="small" label={f.label} value={edit[f.key]}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setEdit((cur) => ({ ...cur, [f.key]: LETTER_KEYS.includes(f.key) ? raw.toUpperCase() : raw }));
                      }}
                      error={!!err}
                      helperText={err || `${f.kind === 'letters' ? LETTERS_HELP : (f.help || '')} Default: ${defText || '(empty)'}.`}
                      inputProps={{ style: f.kind === 'letters' ? { fontFamily: 'monospace' } : undefined }}
                    />
                  );
                })}
              </Stack>
              {editError && <Alert severity="error" sx={{ mt: 2 }}>{editError}</Alert>}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEdit(null)} disabled={editBusy}>Cancel</Button>
              <Button variant="contained" sx={primaryButtonSx} onClick={save} disabled={editBusy || !editOk}>
                {editBusy ? <CircularProgress size={20} color="inherit" /> : 'Save'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={resetOpen} busy={resetBusy} onClose={() => setResetOpen(false)} onConfirm={doReset}
        title="Reset the room type rules to the defaults?" confirmLabel="Reset" confirmColor="warning"
      >
        <Typography>
          Rounds up to 6R use living rooms (accent chair), larger rounds use dining rooms, shag -2 / -3 accent rugs use
          bedrooms, other accent rugs never use bedrooms and shag runners use hallways. Unstarted plans are re-checked within
          a few minutes.
        </Typography>
      </ConfirmDialog>
    </Paper>
  );
};

export default RoomRulesPanel;
